<template>
  <EquipmentBase :section-id="ScenarioDetailSectionKey.SURFACE_DRIVE">
    <EquipmentHeader>
      <template #title>
        <div class="flex justify-content-between align-items-center mb-2">
          Surface Drive
          <CalculatorToggle
            :section-ids="[EquipmentCalculatorSectionKey.SURFACE_DRIVE]"
          />
        </div>
      </template>
      <template #titleRight>
        <a
          v-if="isEspOnShore"
          href="/assets/Summit MaxGround Flyer.pdf"
          download="Summit%20MaxGround%20Flyer.pdf"
        >
          <icon :icon="['far', 'link']" class="mr-1" />Max Ground Preview
        </a>
      </template>
    </EquipmentHeader>

    <div class="flex flex-wrap grid justify-content-between mb-4">
      <ConverterInput
        class="mr-4"
        label="Incoming Power"
        :base-unit="VoltageUnits.Volts"
        :model="VoltageUnits.options()"
        mode="write"
        :model-value="parameters.surface_drive_incoming_power_volts"
        @update:model-value="
          parametersForm.update({
            surface_drive_incoming_power_volts: $event
          })
        "
      />
      <div class="col-2">
        <span class="body-4 mb-2">Drive Type</span>
        <div class="flex mx-list-3">
          <div class="p-field-radiobutton">
            <RadioButton
              inputId="vsd-radio"
              :model-value="equipment.use_vsd"
              :disabled="isScenarioReadonly || isImportedFromLegacy"
              class="mr-1"
              :value="true"
              @update:model-value="equipmentForm.update({ use_vsd: true })"
            />
            <label for="vsd-radio">VSD</label>
          </div>
          <div class="p-field-radiobutton">
            <RadioButton
              inputId="switchboard-radio"
              :model-value="!equipment.use_vsd"
              :disabled="isScenarioReadonly || isImportedFromLegacy"
              class="mr-1"
              :value="true"
              @update:model-value="equipmentForm.update({ use_vsd: false })"
            />
            <label for="switchboard-radio">Switchboard</label>
          </div>
        </div>
      </div>
      <div class="col-2">
        <ConverterInput
          label="Transformer Ratio"
          :base-unit="PercentageUnits.Ratio"
          :model="PercentageUnits.options()"
          mode="read"
          class="mr-1"
          :model-value="surfaceDriveCalculations.transformerRatio.value"
        />
      </div>
      <div class="col-2">
        <ConverterInput
          class="mr-4"
          label="Min Hz"
          :base-unit="FrequencyUnits.Hertz"
          :model="FrequencyUnits.options()"
          mode="write"
          :model-value="parameters.minimum_frequency"
          @update:model-value="
            parametersForm.update({
              minimum_frequency: $event
            })
          "
        />
      </div>
      <div class="col-2">
        <ConverterInput
          class="mr-4"
          label="Max Hz"
          :base-unit="FrequencyUnits.Hertz"
          :model="FrequencyUnits.options()"
          mode="write"
          :model-value="parameters.maximum_frequency"
          @update:model-value="
            parametersForm.update({
              maximum_frequency: $event
            })
          "
        />
      </div>
      <div v-if="isEspOnShore">
        <ConverterInput
          class="col"
          label="Cost per kWH"
          :model-value="parameters.cable_cost_kw_hr_usd"
          :base-unit="GenericUnits.UnitedStatesDollar"
          :model="[GenericUnits.UnitedStatesDollar]"
          mode="write"
          @update:model-value="
            parametersForm.update({ cable_cost_kw_hr_usd: $event })
          "
        />
      </div>
    </div>

    <!-- Step Down Transformer -->
    <DownTransRow />

    <!-- VSD -->
    <VsdRow v-if="equipment.use_vsd" />

    <!-- Switchboard -->
    <SwitchboardRow
      v-else
      :equipment="equipment"
      :switchboard="equipment.r_switchboard"
      @update:equipment="equipmentForm.update($event)"
    />

    <!-- Step Up Transformer -->
    <UpTransRow />

    <!-- Stats Chart -->
    <div class="grid">
      <div class="col-6">
        <DataTable class="condensed" :value="stats">
          <Column field="field"></Column>
          <Column field="required" header="Required"></Column>
          <Column field="selected" header="Selected"></Column>
        </DataTable>
      </div>
    </div>
  </EquipmentBase>
</template>

<script setup lang="ts">
import { computed } from "vue";
import ConverterInput from "@/components/ConverterInput.vue";
import { VoltageUnits, switchboardKvFieldToVolts } from "@/libs/units/voltage";
import EquipmentBase from "@/components/scenarios/EquipmentBase.vue";
import EquipmentHeader from "@/components/scenarios/EquipmentHeader.vue";
import VsdRow from "@/components/equipment/vsd/VSDRow.vue";
import SwitchboardRow from "@/components/equipment/switchboards/SwitchboardRow.vue";
import DownTransRow from "@/components/equipment/step_down_transformers/DownTransRow.vue";
import UpTransRow from "@/components/equipment/step_up_transformers/UpTransRow.vue";
import CalculatorToggle from "@/components/scenarios/calculator_sections/CalculatorToggle.vue";
import { injectStrict } from "@/utils/utils";
import {
  equipmentFormKey,
  parametersFormKey,
  scenarioCalculationsKey,
  scenarioFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { ScenarioDetailSectionKey } from "@/composables/scenarioCalculations/useEquipmentCalculatorSections";
import { EquipmentCalculatorSectionKey } from "@/composables/scenarioCalculations/calculationTypes";
import { useEditPermissions } from "@/composables/auth/editPermissions";
import { PercentageUnits } from "@/libs/units/percentage";
import { GenericUnits } from "@/libs/units/generic";
import { FrequencyUnits } from "@/libs/units/frequency";
import { Sizing_Pumping_Type_Enum } from "gql/api";

const equipmentForm = injectStrict(equipmentFormKey);
const equipment = computed(() => equipmentForm.data.value);
const parametersForm = injectStrict(parametersFormKey);
const parameters = computed(() => parametersForm.data.value);
const scenario = injectStrict(scenarioFormKey);

const { surfaceDriveCalculations, motorCalculations } = injectStrict(
  scenarioCalculationsKey
);
const { isScenarioReadonly, isImportedFromLegacy } = useEditPermissions();

const isEspOnShore = computed(() => {
  return (
    scenario?.data.value.pumping_type === Sizing_Pumping_Type_Enum.EspOnShore
  );
});
const stats = computed(() => {
  if (equipmentForm.data.value.use_vsd) {
    return [
      {
        field: "60 Hz Catalog kVA Rating",
        required: surfaceDriveCalculations.requiredKVA60Hz.value.toFixed(2),
        selected: surfaceDriveCalculations.vsdKVARating.value.toFixed(2)
      },
      {
        field: "Max Amp Rating",
        required: surfaceDriveCalculations.maxAmpRatingA.value.toFixed(2),
        selected: equipmentForm.data.value.r_vsd?.max_amps
      }
    ];
  } else {
    return [
      {
        field: "Volt Rating",
        required: Math.max(
          surfaceDriveCalculations.requiredVoltageV.value || 0,
          motorCalculations.adjustedReratedNameplateV.value ||
            motorCalculations.voltageNameplate.value ||
            0
        ).toFixed(2),
        selected: (() => {
          const kv = equipmentForm.data.value.r_switchboard?.kv;
          if (kv === null || kv === undefined) {
            return undefined;
          }
          return switchboardKvFieldToVolts(Number(kv)).toFixed(2);
        })()
      },
      {
        field: "Max Amp Rating",
        required: surfaceDriveCalculations.maxAmpRatingA.value.toFixed(2),
        selected: equipmentForm.data.value.r_switchboard?.amps
      }
    ];
  }
});
</script>
